import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Zap, Clock, Shield, Wind, Thermometer, Leaf, Home, Settings, Wrench, Droplets, Paintbrush, Star } from 'lucide-react';

const RoofCoating = () => {
  return (
    <Layout>
      <div className="w-full">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-brand-charcoal-gray to-brand-charcoal-gray/90 text-white py-20">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Premium Roof Coating Services in Eastern North Carolina</h1>
            <p className="text-xl italic mb-6">Extend roof life, reduce energy costs, and enhance property value with trophy-quality roof coating systems designed for Eastern NC's unique climate</p>
            
            <div className="flex flex-wrap gap-6 mt-8 justify-center">
              <div className="flex items-center bg-white/10 px-4 py-3 rounded">
                <span className="text-lg">15-20 Year System Warranties</span>
              </div>
              <div className="flex items-center bg-white/10 px-4 py-3 rounded">
                <span className="text-lg">Reduce Cooling Costs by 15-35%</span>
              </div>
              <div className="flex items-center bg-white/10 px-4 py-3 rounded">
                <span className="text-lg">Add 10-15 Years of Roof Life</span>
              </div>
              <div className="flex items-center bg-white/10 px-4 py-3 rounded">
                <span className="text-lg">Superior Hurricane Resistance</span>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction Section */}
        <section id="introduction" className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 gap-10">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-center">Extend Your Roof's Life with Trophy-Quality Roof Coating</h2>
                
                <p className="mb-4 text-lg">A professionally applied roof coating is one of the most cost-effective ways to protect and preserve your Eastern North Carolina property. Carolina Trophy Roofs specializes in premium coating systems that not only extend your roof's lifespan but also reduce energy costs and provide superior protection against our region's challenging weather.</p>
                
                <p className="mb-4">According to research from the <a href="https://www.roofcoatings.org/" target="_blank" rel="noopener" className="text-brand-carolina-blue hover:underline">Roof Coatings Manufacturers Association</a>, quality roof coatings can extend roof life by 10-15 years while reducing cooling costs by up to 35% — particularly valuable in Eastern NC's hot, humid climate.</p>
                
                <p>Unlike standard contractors who often apply single-layer coatings with minimal preparation, our trophy-quality approach includes thorough cleaning, expert surface preparation, and multi-layer application systems backed by industry-leading warranties.</p>
                
                <div className="flex flex-wrap gap-4 mt-6 items-center">
                  <div className="flex items-center">
                    <div className="text-brand-trophy-gold text-xl">★★★★★</div>
                    <div className="ml-2 text-sm">52 verified roof coating reviews</div>
                  </div>
                  <div className="h-8 w-px bg-gray-300"></div>
                  <div className="text-sm">ENERGY STAR Partner</div>
                  <div className="h-8 w-px bg-gray-300"></div>
                  <div className="text-sm">Cool Roof Rating Council Resource</div>
                  <div className="h-8 w-px bg-gray-300"></div>
                  <div className="text-sm">Department of Energy Better Buildings Resource</div>
                </div>
              </div>
              
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section id="benefits" className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-10 text-center">The Carolina Trophy Roofs Coating Difference</h2>
            
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 text-center">Premium vs. Standard Roof Coating Comparison</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-brand-charcoal-gray text-white">
                      <th className="p-4 text-left">Feature</th>
                      <th className="p-4 text-left">Standard Contractor Approach</th>
                      <th className="p-4 text-left">Trophy-Quality Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white">
                      <td className="p-4 border-b font-medium">Surface Preparation</td>
                      <td className="p-4 border-b">Basic power washing only</td>
                      <td className="p-4 border-b">Thorough cleaning, substrate repair, primer application, and moisture testing</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-4 border-b font-medium">Application Process</td>
                      <td className="p-4 border-b">Single coat application</td>
                      <td className="p-4 border-b">Multi-layer system with base coat, intermediate coat, and UV-resistant topcoat</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="p-4 border-b font-medium">Material Quality</td>
                      <td className="p-4 border-b">Standard acrylic coatings</td>
                      <td className="p-4 border-b">Premium silicone or elastomeric formulations with enhanced adhesion</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-4 border-b font-medium">Energy Efficiency</td>
                      <td className="p-4 border-b">Basic reflectivity</td>
                      <td className="p-4 border-b">Advanced thermal emissivity coatings with 85%+ solar reflectance</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="p-4 border-b font-medium">Weather Resistance</td>
                      <td className="p-4 border-b">Moderate wind and rain protection</td>
                      <td className="p-4 border-b">Hurricane-grade adhesion, hail-resistant formulation, wind-rated to 130+ mph</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-4 border-b font-medium">Expected Lifespan</td>
                      <td className="p-4 border-b">5-8 years</td>
                      <td className="p-4 border-b">15-25 years with proper maintenance</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="p-4 border-b font-medium">Warranty</td>
                      <td className="p-4 border-b">0-5 years, workmanship only</td>
                      <td className="p-4 border-b">15-20 year comprehensive manufacturer + workmanship warranties</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="mt-4 text-sm text-gray-500 text-center">Based on comparative data from the <a href="https://www.nrca.net/" target="_blank" rel="noopener" className="text-brand-carolina-blue hover:underline">National Roofing Contractors Association</a> and the <a href="https://www.coolroofs.org/" target="_blank" rel="noopener" className="text-brand-carolina-blue hover:underline">Cool Roof Rating Council</a></p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
                  <Zap className="h-20 w-20 text-yellow-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3">Significant Energy Savings</h3>
                  <p className="text-gray-600 mb-4">According to the U.S. Department of Energy, reflective roof coatings can reduce cooling costs by 15-35% in hot climates like Eastern North Carolina. Our premium coatings reflect up to 85% of solar radiation, dramatically reducing heat transfer to your building.</p>
                  <p className="text-sm text-gray-500">Our coating systems meet or exceed ENERGY STAR® requirements with Solar Reflectance Index (SRI) values of 100+, significantly higher than the minimum 75 SRI requirement.</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
                  <Clock className="h-20 w-20 text-blue-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3">Extended Roof Lifespan</h3>
                  <p className="text-gray-600 mb-4">Quality roof coatings create a seamless, waterproof membrane that protects your existing roof from UV damage, thermal shock, and moisture penetration. This protection can add 10-15 years of functional life to your roof system without the disruption and expense of replacement.</p>
                  <p className="text-sm text-gray-500">Our systems incorporate elastomeric properties allowing for 200%+ elongation to accommodate thermal expansion and contraction cycles common in Eastern NC's climate.</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
                  <Shield className="h-20 w-20 text-cyan-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3">Superior Leak Prevention</h3>
                  <p className="text-gray-600 mb-4">Premium roof coatings create a monolithic, seamless membrane that bridges small cracks and seals potential entry points for water. This is particularly valuable for Eastern NC's intense rainfall events, where water can exploit even minor vulnerabilities.</p>
                  <p className="text-sm text-gray-500">Our high-build coating systems create a minimum 30-mil dry film thickness (3x thicker than standard applications) for enhanced leak protection.</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
                  <Wind className="h-20 w-20 text-purple-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3">Enhanced Hurricane Resistance</h3>
                  <p className="text-gray-600 mb-4">Eastern North Carolina's exposure to tropical systems demands superior protection. Our premium coating systems improve wind uplift resistance and create a more stable roof system that better withstands hurricane conditions.</p>
                  <p className="text-sm text-gray-500">Our coating systems are tested to ASTM D6083 standards and meet Miami-Dade County Product Control approval requirements for high-velocity hurricane zones.</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
                  <Thermometer className="h-20 w-20 text-red-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3">Reduced Thermal Expansion</h3>
                  <p className="text-gray-600 mb-4">Eastern NC's dramatic temperature swings cause roofing materials to expand and contract, creating stress that leads to premature aging. Reflective coatings can reduce roof surface temperatures by 50-60°F, minimizing this harmful cycle.</p>
                  <p className="text-sm text-gray-500">Surface temperature monitoring on our projects shows peak summer temperature reductions from 165°F to below 105°F after coating application.</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-48 bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
                  <Leaf className="h-20 w-20 text-green-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-3">Environmentally Friendly Solution</h3>
                  <p className="text-gray-600 mb-4">Roof coating is a sustainable alternative to roof replacement, eliminating the need to dispose of old roofing materials in landfills. Many of our coating options are low-VOC or zero-VOC formulations that comply with strict environmental standards.</p>
                  <p className="text-sm text-gray-500">Our premium coating systems can contribute to LEED points in several categories including Energy & Atmosphere, Materials & Resources, and Indoor Environmental Quality.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-6 text-center">Our Premium Roof Coating Services</h2>
            
            <p className="text-center mb-12 max-w-3xl mx-auto">Carolina Trophy Roofs offers specialized coating solutions tailored to your specific roof type and condition. All services include our comprehensive 7-step preparation process to ensure maximum coating adhesion and longevity.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Rubberized Roof Coating",
                  description: "Our premium rubberized coating systems provide superior elasticity and durability, ideal for roofs that experience significant thermal movement or have numerous penetrations. These coatings can stretch up to 500% and return to their original shape, creating a truly dynamic protection system.",
                  specs: {
                    bestFor: "Flat commercial roofs, aging modified bitumen, EPDM membrane systems",
                    longevity: "15-20 years",
                    warranty: "15-year manufacturer + 5-year workmanship"
                  },
                  link: "/services/roof-coating/rubber/"
                },
                {
                  title: "Metal Roof Coating",
                  description: "Our specialized metal roof coating systems are formulated to address the unique challenges of metal roofing, including rust prevention, fastener sealing, and thermal movement. These coatings create a water-tight membrane while preserving the distinctive profile of metal roofing.",
                  specs: {
                    bestFor: "Metal roofs of all types, agricultural buildings, commercial warehouses",
                    longevity: "15-25 years",
                    warranty: "20-year manufacturer + 5-year workmanship"
                  },
                  link: "/services/roof-coating/metal/"
                },
                {
                  title: "Commercial Roof Coating",
                  description: "Our commercial-grade coating systems are engineered for large-scale applications with enhanced durability and minimal business disruption. These systems are particularly effective at reducing energy loads for facilities with high cooling demands.",
                  specs: {
                    bestFor: "Warehouses, shopping centers, manufacturing facilities, schools",
                    longevity: "12-20 years depending on system",
                    warranty: "12-20 year manufacturer + 5-year workmanship"
                  },
                  link: "/services/roof-coating/commercial/"
                },
                {
                  title: "Premium Roof Waterproofing",
                  description: "Our specialized waterproofing systems go beyond standard coatings with enhanced moisture barriers and hydrophobic properties. These systems are particularly valuable for low-slope residential roofs and areas prone to standing water.",
                  specs: {
                    bestFor: "Residential flat roofs, low-slope structures, areas with poor drainage",
                    longevity: "10-15 years",
                    warranty: "10-year manufacturer + 5-year workmanship"
                  },
                  link: "/services/roof-waterproofing/"
                },
                {
                  title: "Premium Roof Sealant Systems",
                  description: "Our advanced sealant systems target specific vulnerable areas of your roof, including penetrations, transitions, and flashings. These systems can be applied independently or as part of a comprehensive coating treatment.",
                  specs: {
                    bestFor: "Preventative maintenance, leak-prone areas, chimney and vent protection",
                    longevity: "8-12 years",
                    warranty: "10-year manufacturer + 3-year workmanship"
                  },
                  link: "/services/roof-sealant/"
                },
                {
                  title: "Specialty Coating Applications",
                  description: "For unique roofing situations, we offer specialized coating solutions including cool roof systems, sound-dampening applications, chemical-resistant formulations, and high-traffic protective coatings.",
                  specs: {
                    bestFor: "Special use buildings, unusual roof types, extreme conditions",
                    longevity: "Varies by system",
                    warranty: "System-specific"
                  },
                  link: "/services/roof-coating/specialty/"
                }
              ].map((service, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                  <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                    {index === 0 && <Home className="h-20 w-20 text-gray-600" />}
                    {index === 1 && <Settings className="h-20 w-20 text-gray-600" />}
                    {index === 2 && <Wrench className="h-20 w-20 text-gray-600" />}
                    {index === 3 && <Droplets className="h-20 w-20 text-blue-500" />}
                    {index === 4 && <Paintbrush className="h-20 w-20 text-orange-500" />}
                    {index === 5 && <Star className="h-20 w-20 text-purple-500" />}
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-xl mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="bg-gray-50 p-4 rounded mb-4">
                      <p className="text-sm">
                        <strong>Best For:</strong> {service.specs.bestFor}<br />
                        <strong>Longevity:</strong> {service.specs.longevity}<br />
                        <strong>Warranty:</strong> {service.specs.warranty}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section id="process" className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-6 text-center">Our 7-Step Trophy-Quality Roof Coating Process</h2>
            
            <p className="text-center mb-12 max-w-3xl mx-auto">Based on best practices recommended by the <a href="https://www.nrca.net/resources" target="_blank" rel="noopener" className="text-brand-carolina-blue hover:underline">National Roofing Contractors Association</a> and major coating manufacturers, our comprehensive application process ensures maximum coating performance and longevity.</p>
            
            <div className="space-y-8">
              {[
                {
                  number: 1,
                  title: "Thorough Roof Inspection",
                  description: "We begin with a comprehensive roof assessment to identify existing conditions, potential issues, and specific needs that will inform our coating recommendation. This includes moisture scanning, core sampling when necessary, and detailed documentation of the current state.",
                  detail: "Our inspection protocols follow guidelines from the International Institute of Building Enclosure Consultants and include infrared scanning to detect subsurface moisture not visible to the naked eye."
                },
                {
                  number: 2,
                  title: "Customized System Design",
                  description: "Based on your roof's specific conditions, we develop a tailored coating system that addresses your particular needs, budget, and performance requirements. This includes material selection, application specifications, and warranty options.",
                  detail: "Our system designs incorporate data from the Cool Roof Rating Council and material performance studies specific to Eastern North Carolina's climate challenges."
                },
                {
                  number: 3,
                  title: "Comprehensive Surface Preparation",
                  description: "Unlike standard contractors who often skip this critical step, we thoroughly clean and prepare your roof surface using a multi-phase approach. This includes power washing with specialized cleaning agents, removal of contaminants, and addressing surface irregularities.",
                  detail: "Our preparation exceeds ASTM D6083 requirements for coating adhesion, with surface profile testing to verify optimal conditions before application begins."
                },
                {
                  number: 4,
                  title: "Targeted Repairs & Reinforcement",
                  description: "We address all existing issues before coating application, including seam reinforcement, fastener replacement, minor repairs, and treatment of rust or other deterioration. This creates a sound substrate for the coating system.",
                  detail: "Our repair methods align with technical recommendations from the Spray Polyurethane Foam Alliance and manufacturer-specific protocols for each substrate type."
                },
                {
                  number: 5,
                  title: "Prime Coat Application",
                  description: "Unlike standard contractors who often skip primer, we apply a specialized base coat designed to enhance adhesion between your roof surface and the main coating. This critical step dramatically improves system performance and longevity.",
                  detail: "Our primer selection follows the Roof Coatings Manufacturers Association's technical recommendations for specific substrate compatibility and performance enhancement."
                },
                {
                  number: 6,
                  title: "Multi-Layer Coating Application",
                  description: "We apply the main coating system in multiple layers to ensure proper mil thickness, complete coverage, and optimal curing. This methodical approach creates a monolithic membrane without thin spots or application defects.",
                  detail: "Our application methods maintain strict adherence to manufacturer specifications for atmospheric conditions, including temperature, humidity, and dew point monitoring throughout the application process."
                },
                {
                  number: 7,
                  title: "Quality Verification & Documentation",
                  description: "Upon completion, we perform detailed quality assurance testing, including mil thickness measurement, adhesion testing, and visual inspection. You receive comprehensive documentation of the work performed, warranty information, and maintenance recommendations.",
                  detail: "Our quality control procedures include dry film thickness measurements at 1 per 500 square feet minimum and adhesion testing that exceeds ASTM D6083 requirements for system integrity."
                }
              ].map((step, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-brand-trophy-gold text-white flex items-center justify-center text-xl font-bold">
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                    <p className="mb-3">{step.description}</p>
                    <p className="text-sm text-gray-600 bg-gray-100 p-3 rounded">{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-10 text-center">Recent Roof Coating Projects</h2>
            
            <Tabs defaultValue="residential" className="w-full">
              <TabsList className="w-full flex justify-center mb-8">
                <TabsTrigger value="residential">Residential</TabsTrigger>
                <TabsTrigger value="commercial">Commercial</TabsTrigger>
                <TabsTrigger value="metal">Metal Roof</TabsTrigger>
              </TabsList>
              
              <TabsContent value="residential">
                <div className="grid grid-cols-1 gap-8">
                  <div>
                    <h3 className="text-2xl font-bold mb-1">Shingle Roof Preservation System</h3>
                    <p className="text-gray-500 mb-3">Wilson, NC</p>
                    <p className="mb-3">This 12-year-old architectural shingle roof was showing signs of premature aging due to intense sun exposure. The homeowner wanted to extend the roof's life without the cost and disruption of replacement.</p>
                    <p className="mb-4"><strong>Trophy Solution:</strong> After thorough cleaning and targeted repairs, we applied our premium shingle preservation coating system with enhanced UV protection. This system sealed the granule loss, reinforced aging shingles, and provided a reflective barrier against further sun damage.</p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">15+</div>
                        <div className="text-sm">Years Added to Roof Life</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">28%</div>
                        <div className="text-sm">Summer Cooling Cost Reduction</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">55°F</div>
                        <div className="text-sm">Roof Surface Temperature Reduction</div>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="commercial">
                <div className="grid grid-cols-1 gap-8">
                  <div>
                    <h3 className="text-2xl font-bold mb-1">Retail Center Energy-Saving System</h3>
                    <p className="text-gray-500 mb-3">Greenville, NC</p>
                    <p className="mb-3">This 45,000 sq. ft. retail center was experiencing significant energy costs and recurring leaks at seams and penetrations. The property management company needed a solution that would minimize business disruption while resolving these issues.</p>
                    <p className="mb-4"><strong>Trophy Solution:</strong> We implemented our commercial-grade silicone coating system with enhanced reflectivity. The process included comprehensive seam reinforcement, replacement of deteriorated fasteners, and treatment of all penetrations before applying a multi-layer coating system.</p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">32%</div>
                        <div className="text-sm">Reduction in Summer Energy Costs</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">0</div>
                        <div className="text-sm">Leaks Since Installation (2+ Years)</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">20</div>
                        <div className="text-sm">Year System Warranty</div>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="metal">
                <div className="grid grid-cols-1 gap-8">
                  <div>
                    <h3 className="text-2xl font-bold mb-1">Agricultural Metal Roof Restoration</h3>
                    <p className="text-gray-500 mb-3">Raleigh, NC</p>
                    <p className="mb-3">This 30-year-old metal roof on a large agricultural facility was experiencing significant rusting, fastener failure, and leaks during heavy rains. The owner needed a solution that would avoid the high cost and operational disruption of replacement.</p>
                    <p className="mb-4"><strong>Trophy Solution:</strong> We implemented our comprehensive metal roof restoration system, which included removal of surface rust, replacement of all fasteners, reinforcement of all seams, and application of our premium metal roof coating system with integrated rust inhibitors.</p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">25+</div>
                        <div className="text-sm">Years of Extended Service Life</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">100%</div>
                        <div className="text-sm">Elimination of Leak Issues</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded text-center">
                        <div className="font-bold text-xl text-brand-trophy-gold">68%</div>
                        <div className="text-sm">Cost Savings vs. Replacement</div>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-10 text-center">Roof Coating: Frequently Asked Questions</h2>
            
            <Accordion type="single" collapsible className="w-full space-y-4">
              <AccordionItem value="item-1" className="border rounded-lg overflow-hidden shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-lg">
                  How much does roof coating cost in North Carolina?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="mb-4">In Eastern North Carolina, professional roof coating typically costs between $1.25-$4.00 per square foot, depending on several factors:</p>
                  <ul className="list-disc pl-6 mb-4 space-y-1">
                    <li>Roof condition and required preparation</li>
                    <li>Coating type (acrylic, silicone, or rubberized)</li>
                    <li>Existing issues requiring repair</li>
                    <li>Roof complexity and accessibility</li>
                    <li>System warranty length</li>
                  </ul>
                  <p className="mb-4">For an average 2,000 sq. ft. home, expect to invest $2,500-$8,000 for a complete premium roof coating system. Commercial applications typically range from $1.00-$3.50 per square foot depending on scale and specifications.</p>
                  <p className="mb-4">Premium silicone and elastomeric coatings cost 30-40% more than standard acrylic but provide 50-100% longer lifespan and superior performance in Eastern NC's climate.</p>
                  <Link to="/services/roof-coating/cost-guide/" className="text-brand-carolina-blue hover:underline font-medium">View Detailed Pricing Guide</Link>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border rounded-lg overflow-hidden shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-lg">
                  How long does roof coating last?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="mb-4">Professionally applied roof coatings in Eastern North Carolina typically last 10-25 years, depending on several factors:</p>
                  <ul className="list-disc pl-6 mb-4 space-y-1">
                    <li><strong>Coating Type:</strong> Standard acrylic coatings generally last 7-12 years, while premium silicone and elastomeric coatings can protect for 15-25 years</li>
                    <li><strong>Application Quality:</strong> Proper surface preparation significantly impacts longevity, with professionally applied coatings lasting up to 40% longer than DIY applications</li>
                    <li><strong>Roof Conditions:</strong> Underlying roof condition, drainage, and exposure to elements all affect coating performance</li>
                    <li><strong>Maintenance:</strong> Simple biannual inspections and cleaning can extend coating life by 20-30%</li>
                  </ul>
                  <p className="mb-4">Carolina Trophy Roofs' premium roof coating systems include proper surface preparation, primer application, and multiple coating layers for maximum durability. Most of our systems come with 10-20 year manufacturer warranties.</p>
                  <Link to="/services/roof-coating/lifespan-guide/" className="text-brand-carolina-blue hover:underline font-medium">View Coating Lifespan Guide</Link>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border rounded-lg overflow-hidden shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-lg">
                  Does roof coating really work?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="mb-4">Yes, professionally applied roof coatings are highly effective at extending roof life and improving energy efficiency. Research and performance data confirm their effectiveness:</p>
                  <ul className="list-disc pl-6 mb-4 space-y-1">
                    <li>According to the U.S. Department of Energy, reflective roof coatings can reduce cooling costs by 15-35% in hot climates like Eastern North Carolina</li>
                    <li>Studies from the Roof Coatings Manufacturers Association show that quality coatings can extend roof lifespan by 10-15 years</li>
                    <li>Roof coatings create a seamless waterproof barrier that prevents leaks and protects against UV radiation</li>
                    <li>Premium coatings can reflect up to 85% of solar radiation, potentially lowering roof surface temperatures by 50-60°F during peak summer conditions</li>
                    <li>Coatings reduce thermal expansion stress that leads to premature aging and material failure</li>
                  </ul>
                  <p className="mb-4">The effectiveness of coating systems depends significantly on proper application and material selection. Professional application with appropriate preparation is critical for achieving maximum performance.</p>
                  <Link to="/services/roof-coating/performance-data/" className="text-brand-carolina-blue hover:underline font-medium">View Performance Data</Link>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border rounded-lg overflow-hidden shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-lg">
                  Can roof coating stop leaks?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="mb-4">Yes, properly applied roof coatings can effectively stop and prevent leaks when installed correctly. Here's how they work:</p>
                  <ul className="list-disc pl-6 mb-4 space-y-1">
                    <li>Premium elastomeric and silicone coatings create a seamless, waterproof membrane that can seal small cracks and prevent water infiltration</li>
                    <li>For existing leaks, proper preparation including crack sealing, fastener tightening, and targeted repairs must be completed before coating application</li>
                    <li>According to the Roof Coatings Manufacturers Association, professionally applied roof coatings can bridge gaps up to 1/16 inch and expand/contract with temperature fluctuations to maintain a watertight seal</li>
                    <li>Specialized reinforcement fabrics can be incorporated at seams, transitions, and problem areas to provide additional leak protection</li>
                  </ul>
                  <p className="mb-4">Important note: Coatings are not suitable for roofs with structural damage or large holes, which require repair before coating. A professional assessment is necessary to determine if your specific leak issues can be resolved with coating.</p>
                  <Link to="/services/roof-coating/leak-solutions/" className="text-brand-carolina-blue hover:underline font-medium">View Leak Repair Solutions</Link>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border rounded-lg overflow-hidden shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-lg">
                  What roof coating is best for Eastern North Carolina's climate?
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="mb-4">For Eastern North Carolina's specific climate challenges, these coating types perform best:</p>
                  <ul className="list-disc pl-6 mb-4 space-y-1">
                    <li><strong>Silicone Coatings:</strong> Excellent for our region's heavy rainfall and hurricane conditions. Silicone maintains effectiveness in ponding water conditions and provides excellent UV resistance, crucial for Eastern NC's 215+ sunny days per year</li>
                    <li><strong>High-Build Elastomeric Coatings:</strong> Superior crack-bridging ability important for seasonal temperature fluctuations (20°F to 95°F+ throughout the year). These coatings accommodate the expansion and contraction common in our climate</li>
                    <li><strong>Specialized Metal Roof Coatings:</strong> For metal roofs common in our region, formulations with rust inhibitors provide the best protection against coastal salt air exposure</li>
                    <li><strong>Reflective Acrylic Coatings:</strong> For budget-conscious projects, high-quality acrylics with enhanced reflectivity work well for moderate protection needs, though they typically require more frequent reapplication</li>
                  </ul>
                  <p className="mb-4">Carolina Trophy Roofs carefully selects coating systems based on specific roof types and local microclimate factors to ensure optimal performance. We consider factors like coastal proximity, tree coverage, and building orientation in our recommendations.</p>
                  <Link to="/services/roof-coating/climate-guide/" className="text-brand-carolina-blue hover:underline font-medium">View NC Climate Considerations</Link>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

      </div>
    </Layout>
  );
};

export default RoofCoating;
